import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Plus, Trash2, Edit2, Wallet, X, Paperclip, Image as ImageIcon, ChevronLeft, ChevronRight, Banknote, RotateCcw, Check,
  Home as HomeIcon, List as ListIcon, PieChart as PieIcon, Tag, Search, RefreshCw, CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';
import { moneyAPI, optionsAPI } from '../services/api';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';
import { AppSheet, SheetRow } from '../components/common/AppViewKit';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const INCOME_CATS = [
  { value: 'collection',    label: 'Customer Collection', color: '#10b981' },
  { value: 'installation',  label: 'Installation Fee',    color: 'var(--accent)' },
  { value: 'advance',       label: 'Advance Payment',     color: 'var(--primary)' },
  { value: 'reconnection',  label: 'Reconnection Fee',    color: '#f59e0b' },
  { value: 'other_income',  label: 'Other Income',        color: '#64748b' },
];

const EXPENSE_CATS = [
  { value: 'staff_salary',    label: 'Staff Salary',      color: '#ef4444' },
  { value: 'equipment',       label: 'Equipment',         color: '#f97316' },
  { value: 'maintenance',     label: 'Maintenance',       color: '#eab308' },
  { value: 'rent',            label: 'Rent',              color: 'var(--primary)' },
  { value: 'utilities',       label: 'Utilities',         color: 'var(--accent)' },
  { value: 'transport',       label: 'Transport',         color: '#14b8a6' },
  { value: 'office',          label: 'Office Expenses',   color: 'var(--accent)' },
  { value: 'other_expense',   label: 'Other Expense',     color: '#94a3b8' },
];

const ALL_CATS = [...INCOME_CATS, ...EXPENSE_CATS];

// Custom categories (added by a user via optionsAPI) carry a user-picked color, encoded
// as "name::#hexcolor" in the stored option string. Older entries saved before the color
// picker existed are plain names with no "::" — hash those to a stable palette pick so
// they still render consistently.
const CUSTOM_PALETTE = ['#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f43f5e', '#0ea5e9', '#d946ef', '#22c55e', '#ef4444', '#10b981', '#f59e0b', '#3b82f6'];
const colorForCustom = (v) => {
  let hash = 0;
  for (let i = 0; i < v.length; i++) hash = (hash * 31 + v.charCodeAt(i)) >>> 0;
  return CUSTOM_PALETTE[hash % CUSTOM_PALETTE.length];
};
const parseCatOption = (raw) => {
  const i = raw.lastIndexOf('::');
  if (i === -1) return { value: raw, label: raw, color: colorForCustom(raw) };
  const name = raw.slice(0, i);
  return { value: name, label: name, color: raw.slice(i + 2) };
};
// customColors: optional { [categoryName]: color } map for custom categories, so the
// pie chart / transaction list reflect the color a user actually picked.
const getCat = (v, customColors) => ALL_CATS.find(c => c.value === v)
  || { label: v, color: (customColors && customColors[v]) || colorForCustom(String(v || '')) };

const monthLabel = (offset) => {
  const d = new Date();
  d.setDate(1); // pin to the 1st before shifting months, so short months don't roll over
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const PERIODS = [
  { value: 'week',  label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year',  label: 'This Year' },
  { value: 'all',   label: 'All Time' },
];

const METHODS = ['cash', 'upi', 'bank_transfer', 'cheque', 'other'];

function groupByDate(transactions) {
  const groups = {};
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  transactions.forEach(t => {
    const d = new Date(t.date);
    let label;
    if (d.toDateString() === today.toDateString()) label = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    if (!groups[label]) groups[label] = [];
    groups[label].push(t);
  });
  return groups;
}

/* ─── Mobile app ─────────────────────────────────────────────────────────────── */
// Full-screen finance app for App View: own header, 4 bottom tabs (Home, Activity, Reports,
// Categories) and a centre "+" button. Rendered through a portal so it sits above the CRM's
// shell; the back arrow returns to the CRM. All data/handlers come from MoneyManagerPage.
const ink = { income: 'var(--c-15803d)', expense: 'var(--c-dc2626)' };
const sgn = (t) => (t === 'income' ? '+' : '−');

function TxnRow({ txn, cat, onClick, first }) {
  return (
    <button type="button" onClick={onClick}
      style={{ width: '100%', minHeight: 60, display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', border: 'none',
        borderTop: first ? 'none' : '1px solid var(--border)', background: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--text)' }}>
      <span style={{ width: 40, height: 40, borderRadius: 13, background: cat.color + '22', color: cat.color, display: 'grid', placeItems: 'center', fontWeight: 800, flexShrink: 0 }}>
        {String(cat.label || '?').charAt(0).toUpperCase()}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, fontSize: '.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{txn.description}</span>
        <span style={{ display: 'block', fontSize: '.74rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cat.label} · <span style={{ textTransform: 'capitalize' }}>{txn.paymentMethod?.replace('_', ' ')}</span>
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0 }}>
        <span style={{ display: 'block', fontWeight: 800, fontSize: '.96rem', color: ink[txn.type] }}>{sgn(txn.type)}₹{fmt(txn.amount)}</span>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, fontSize: '.7rem', color: 'var(--text-muted)' }}>
          {txn.receiptUrl && <Paperclip size={11} />}
          {txn.checked && <Check size={12} color="var(--c-15803d)" />}
          {new Date(txn.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </span>
    </button>
  );
}

const Card = ({ children, style }) => (
  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, ...style }}>{children}</div>
);
const Seg = ({ value, onChange, options }) => (
  <div style={{ display: 'flex', background: 'var(--cb-f1f5f9)', borderRadius: 12, padding: 4, gap: 4 }}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        style={{ flex: 1, minHeight: 36, border: 'none', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontSize: '.83rem',
          background: value === v ? 'var(--bg-card)' : 'transparent', color: value === v ? 'var(--primary)' : 'var(--text-muted)',
          boxShadow: value === v ? '0 1px 4px rgba(0,0,0,.12)' : 'none' }}>{l}</button>
    ))}
  </div>
);

function MoneyApp({ summary, transactions, loading, period, changePeriod, monthOffset, setMonthOffset, periodLabel, customCatColors, setCustomCatColors, modal, setModal, load, toggleChecked, handleDelete }) {
  const modalOpen = !!modal;
  const [tab, setTab] = useState('home');
  const [typeF, setTypeF] = useState('all');
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState(null);
  const [repType, setRepType] = useState('expense');
  const [sheet, setSheet] = useState(null); // 'period' | 'cash' | 'newcat'
  const [actionTxn, setActionTxn] = useState(null);
  const [cashTotal, setCashTotal] = useState(0);
  const [custom, setCustom] = useState({ income: [], expense: [] });
  const [nc, setNc] = useState({ type: 'expense', name: '', color: CUSTOM_PALETTE[0] });

  useEffect(() => {
    Promise.all([optionsAPI.get('moneyCategories_income'), optionsAPI.get('moneyCategories_expense')])
      .then(([i, e]) => setCustom({ income: (i.data.data || []).map(parseCatOption), expense: (e.data.data || []).map(parseCatOption) }))
      .catch(() => {});
  }, [modalOpen]); // re-read after the add form closes, so a category made there shows up in Categories

  const income = summary?.periodIncome || 0;
  const expense = summary?.periodExpense || 0;
  const net = income - expense;
  const catOf = (v) => getCat(v, customCatColors);
  const breakdown = (summary?.categoryBreakdown || [])
    .map(b => ({ key: b._id.category, type: b._id.type, total: b.total, ...catOf(b._id.category) }))
    .sort((a, b) => b.total - a.total);

  const goActivity = (type, cat) => { setTypeF(type || 'all'); setCatFilter(cat || null); setQ(''); setTab('activity'); };
  const openCash = () => {
    const c = loadCashCounterCounts();
    setCashTotal(DENOMINATIONS.reduce((s, d) => s + d * (c[d] || 0), 0));
    setSheet('cash');
  };
  const saveNewCat = async () => {
    const n = nc.name.trim();
    if (!n) return;
    try {
      await optionsAPI.add(`moneyCategories_${nc.type}`, `${n}::${nc.color}`);
      setCustom(c => ({ ...c, [nc.type]: [...c[nc.type], { value: n, label: n, color: nc.color }] }));
      setCustomCatColors(prev => ({ ...prev, [n]: nc.color }));
      setNc({ type: nc.type, name: '', color: CUSTOM_PALETTE[0] });
      setSheet(null);
      toast.success('Category added');
    } catch { toast.error('Failed to add category'); }
  };

  const list = transactions.filter(t =>
    (typeF === 'all' || t.type === typeF)
    && (!catFilter || t.category === catFilter)
    && (!q || `${t.description} ${t.reference || ''} ${catOf(t.category).label}`.toLowerCase().includes(q.toLowerCase())));

  const renderGroups = (items) => Object.entries(groupByDate(items)).map(([label, txns]) => {
    const di = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const de = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return (
      <div key={label} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 4px 6px', fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)' }}>
          <span style={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</span>
          <span style={{ display: 'flex', gap: 10 }}>
            {di > 0 && <span style={{ color: ink.income }}>+₹{fmt(di)}</span>}
            {de > 0 && <span style={{ color: ink.expense }}>−₹{fmt(de)}</span>}
          </span>
        </div>
        <Card style={{ overflow: 'hidden' }}>
          {txns.map((t, i) => <TxnRow key={t._id} txn={t} cat={catOf(t.category)} first={i === 0} onClick={() => setActionTxn(t)} />)}
        </Card>
      </div>
    );
  });

  const empty = (msg) => (
    <div style={{ textAlign: 'center', padding: '36px 20px', color: 'var(--text-muted)' }}>
      <Wallet size={38} style={{ marginBottom: 8, opacity: .25 }} />
      <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{msg}</div>
    </div>
  );
  const spin = <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  const bar = (pct, color) => (
    <div style={{ height: 6, borderRadius: 3, background: 'var(--cb-f1f5f9)', overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(2, Math.min(100, pct))}%`, height: '100%', background: color, borderRadius: 3 }} />
    </div>
  );

  /* ── Home ── */
  const home = (
    <>
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, var(--primary) 100%)', color: '#fff', borderRadius: 20, padding: '18px 18px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: '.72rem', fontWeight: 700, opacity: .7, textTransform: 'uppercase', letterSpacing: '.07em' }}>Available balance</div>
        <div style={{ fontSize: '2.3rem', fontWeight: 900, letterSpacing: '-.02em', lineHeight: 1.1, margin: '2px 0 14px' }}>₹{fmt(summary?.balance || 0)}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button type="button" onClick={() => goActivity('income')} style={{ textAlign: 'left', border: 'none', color: '#fff', background: 'rgba(16,185,129,.24)', borderRadius: 14, padding: '10px 12px', cursor: 'pointer' }}>
            <span style={{ fontSize: '.68rem', fontWeight: 700, opacity: .85, display: 'flex', alignItems: 'center', gap: 4 }}><TrendingUp size={12} /> INCOME</span>
            <span style={{ display: 'block', fontSize: '1.15rem', fontWeight: 800 }}>₹{fmt(income)}</span>
          </button>
          <button type="button" onClick={() => goActivity('expense')} style={{ textAlign: 'left', border: 'none', color: '#fff', background: 'rgba(239,68,68,.28)', borderRadius: 14, padding: '10px 12px', cursor: 'pointer' }}>
            <span style={{ fontSize: '.68rem', fontWeight: 700, opacity: .85, display: 'flex', alignItems: 'center', gap: 4 }}><TrendingDown size={12} /> EXPENSE</span>
            <span style={{ display: 'block', fontSize: '1.15rem', fontWeight: 800 }}>₹{fmt(expense)}</span>
          </button>
        </div>
        <div style={{ fontSize: '.76rem', opacity: .8, marginTop: 10 }}>{periodLabel} · net {net < 0 ? '−' : '+'}₹{fmt(Math.abs(net))}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { l: 'Income', c: ink.income, I: TrendingUp, on: () => setModal({ type: 'income' }) },
          { l: 'Expense', c: ink.expense, I: TrendingDown, on: () => setModal({ type: 'expense' }) },
          { l: 'Count cash', c: 'var(--primary)', I: Banknote, on: openCash },
        ].map(a => (
          <button key={a.l} type="button" onClick={a.on}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '12px 4px', cursor: 'pointer', color: 'var(--text)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: '.78rem' }}>
            <span style={{ width: 38, height: 38, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'var(--cb-f1f5f9)', color: a.c }}><a.I size={19} /></span>
            {a.l === 'Count cash' ? a.l : `+ ${a.l}`}
          </button>
        ))}
      </div>

      {breakdown.some(b => b.type === 'expense') && (
        <Card style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <b style={{ fontSize: '.92rem' }}>Top spending</b>
            <button type="button" onClick={() => setTab('reports')} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer' }}>Details</button>
          </div>
          {breakdown.filter(b => b.type === 'expense').slice(0, 4).map(b => (
            <button key={b.key} type="button" onClick={() => goActivity('expense', b.key)} style={{ width: '100%', background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', color: 'var(--text)', textAlign: 'left' }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', marginBottom: 4 }}>
                <span>{b.label}</span><b>₹{fmt(b.total)}</b>
              </span>
              {bar(expense ? (b.total / expense) * 100 : 0, b.color)}
            </button>
          ))}
        </Card>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 2px 8px' }}>
        <b style={{ fontSize: '.95rem' }}>Recent</b>
        <button type="button" onClick={() => goActivity()} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer' }}>See all</button>
      </div>
      {loading ? spin : transactions.length === 0 ? empty('No transactions yet') : (
        <Card style={{ overflow: 'hidden' }}>
          {transactions.slice(0, 5).map((t, i) => <TxnRow key={t._id} txn={t} cat={catOf(t.category)} first={i === 0} onClick={() => setActionTxn(t)} />)}
        </Card>
      )}
    </>
  );

  /* ── Activity ── */
  const activity = (
    <>
      <div style={{ position: 'sticky', top: 40, zIndex: 2, background: 'var(--bg)', margin: '0 -14px 8px', padding: '4px 14px 8px', display: 'grid', gap: 8 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 13, color: 'var(--text-muted)' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search description, reference, category"
            style={{ width: '100%', boxSizing: 'border-box', height: 42, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', padding: '0 12px 0 36px', fontSize: '16px' }} />
        </div>
        <Seg value={typeF} onChange={setTypeF} options={[['all', 'All'], ['income', 'Income'], ['expense', 'Expense']]} />
        {catFilter && (
          <button type="button" onClick={() => setCatFilter(null)}
            style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 30, padding: '0 12px', borderRadius: 999, border: 'none', color: '#fff', fontWeight: 600, fontSize: '.8rem', background: catOf(catFilter).color }}>
            {catOf(catFilter).label} <X size={13} />
          </button>
        )}
      </div>
      <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', margin: '0 4px 8px' }}>{list.length} transaction{list.length !== 1 ? 's' : ''} · {periodLabel}</div>
      {loading ? spin : list.length === 0 ? empty('Nothing matches') : renderGroups(list)}
    </>
  );

  /* ── Reports ── */
  const rep = breakdown.filter(b => b.type === repType);
  const repTotal = repType === 'income' ? income : expense;
  const reports = (
    <>
      <Card style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
          {[['Income', income, ink.income], ['Expense', expense, ink.expense], ['Net', Math.abs(net), net < 0 ? ink.expense : ink.income]].map(([l, v, c]) => (
            <div key={l}>
              <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{l}</div>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: c }}>{l === 'Net' && net < 0 ? '−' : ''}₹{fmt(v)}</div>
            </div>
          ))}
        </div>
        {income > 0 && (
          <div style={{ marginTop: 12 }}>
            {bar((expense / income) * 100, expense > income ? 'var(--c-dc2626)' : 'var(--primary)')}
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 4 }}>{Math.round((expense / income) * 100)}% of income spent</div>
          </div>
        )}
      </Card>
      <div style={{ marginBottom: 12 }}><Seg value={repType} onChange={setRepType} options={[['expense', 'Expenses'], ['income', 'Income']]} /></div>
      {loading ? spin : rep.length === 0 ? empty(`No ${repType} in ${periodLabel}`) : (
        <>
          <Card style={{ padding: '8px 0', marginBottom: 12 }}>
            <div style={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={rep} cx="50%" cy="50%" innerRadius={58} outerRadius={84} dataKey="total" nameKey="label" paddingAngle={3}>
                    {rep.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`₹${fmt(v)}`, '']} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
                <div><div style={{ fontSize: '.68rem', color: 'var(--text-muted)', fontWeight: 700 }}>TOTAL</div><div style={{ fontWeight: 900, fontSize: '1.1rem' }}>₹{fmt(repTotal)}</div></div>
              </div>
            </div>
          </Card>
          <Card style={{ padding: '4px 14px' }}>
            {rep.map((b, i) => (
              <button key={b.key} type="button" onClick={() => goActivity(repType, b.key)}
                style={{ width: '100%', background: 'none', border: 'none', borderTop: i ? '1px solid var(--border)' : 'none', padding: '11px 0', cursor: 'pointer', color: 'var(--text)', textAlign: 'left' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.86rem', marginBottom: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: b.color }} />
                  <span style={{ flex: 1 }}>{b.label}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '.76rem' }}>{repTotal ? Math.round((b.total / repTotal) * 100) : 0}%</span>
                  <b>₹{fmt(b.total)}</b>
                </span>
                {bar(repTotal ? (b.total / repTotal) * 100 : 0, b.color)}
              </button>
            ))}
          </Card>
        </>
      )}
    </>
  );

  /* ── Categories ── */
  const [catType, setCatType] = useState('expense');
  const base = catType === 'income' ? INCOME_CATS : EXPENSE_CATS;
  const allCats = [...base.map(c => ({ ...c, custom: false })), ...custom[catType].filter(c => !base.some(b => b.value === c.value)).map(c => ({ ...c, custom: true }))];
  const categories = (
    <>
      <div style={{ marginBottom: 12 }}><Seg value={catType} onChange={setCatType} options={[['expense', 'Expense'], ['income', 'Income']]} /></div>
      <Card style={{ overflow: 'hidden', marginBottom: 12 }}>
        {allCats.map((c, i) => {
          const spent = breakdown.find(b => b.key === c.value && b.type === catType)?.total || 0;
          return (
            <button key={c.value} type="button" onClick={() => goActivity(catType, c.value)}
              style={{ width: '100%', minHeight: 54, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', border: 'none', borderTop: i ? '1px solid var(--border)' : 'none', background: 'none', cursor: 'pointer', color: 'var(--text)', textAlign: 'left' }}>
              <span style={{ width: 34, height: 34, borderRadius: 11, background: c.color + '22', color: c.color, display: 'grid', placeItems: 'center', fontWeight: 800 }}>{c.label.charAt(0).toUpperCase()}</span>
              <span style={{ flex: 1, fontWeight: 600, fontSize: '.9rem' }}>{c.label}{c.custom && <span style={{ marginLeft: 6, fontSize: '.64rem', color: 'var(--text-muted)', fontWeight: 700 }}>CUSTOM</span>}</span>
              <span style={{ fontWeight: 700, fontSize: '.86rem', color: spent ? ink[catType] : 'var(--text-muted)' }}>{spent ? `₹${fmt(spent)}` : '—'}</span>
              <ChevronRight size={16} color="var(--text-muted)" />
            </button>
          );
        })}
      </Card>
      <button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 48, justifyContent: 'center', fontWeight: 700 }}
        onClick={() => { setNc(n => ({ ...n, type: catType })); setSheet('newcat'); }}>
        <Plus size={17} /> New category
      </button>
    </>
  );

  // Rendered inside the CRM shell: its header and bottom tab bar stay; Money's own
  // sections are a sticky strip at the top, and "+" floats above the shell's tab bar.
  const secBtn = (k, label, I) => (
    <button key={k} type="button" onClick={() => setTab(k)}
      style={{ flex: 1, minHeight: 40, border: 'none', borderRadius: 10, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: '.76rem', fontWeight: 700,
        background: tab === k ? 'var(--primary)' : 'transparent', color: tab === k ? '#fff' : 'var(--text-muted)' }}>
      <I size={15} />{label}
    </button>
  );

  return (
    <div style={{ color: 'var(--text)' }}>
      <div style={{ position: 'sticky', top: -14, zIndex: 3, background: 'var(--bg)', margin: '-14px -14px 10px', padding: '10px 14px 8px' }}>
        <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 }}>
          {secBtn('home', 'Home', HomeIcon)}
          {secBtn('activity', 'Activity', ListIcon)}
          {secBtn('reports', 'Reports', PieIcon)}
          {secBtn('categories', 'Categories', Tag)}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button type="button" onClick={() => setSheet('period')}
          style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40, padding: '0 14px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', minWidth: 0 }}>
          <CalendarDays size={15} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{periodLabel}</span>
        </button>
        <button type="button" onClick={load} aria-label="Refresh" style={{ width: 40, height: 40, display: 'grid', placeItems: 'center', border: '1px solid var(--border)', borderRadius: '50%', background: 'var(--bg-card)', color: 'var(--text-muted)', cursor: 'pointer' }}><RefreshCw size={16} /></button>
      </div>

      <div style={{ paddingBottom: 70 }}>
        {tab === 'home' && home}
        {tab === 'activity' && activity}
        {tab === 'reports' && reports}
        {tab === 'categories' && categories}
      </div>

      {/* Zero-height sticky row: keeps the button pinned just above the shell's tab bar. */}
      <div style={{ position: 'sticky', bottom: 14, height: 0, display: 'flex', justifyContent: 'flex-end', zIndex: 4, pointerEvents: 'none' }}>
        <button type="button" onClick={() => setModal({ type: 'expense' })} aria-label="Add transaction"
          style={{ pointerEvents: 'auto', transform: 'translateY(-100%)', height: 52, padding: '0 20px 0 16px', borderRadius: 26, border: 'none', background: 'var(--primary)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: '.9rem', cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,.28)' }}>
          <Plus size={22} /> Add
        </button>
      </div>

      {sheet === 'period' && (
        <AppSheet title="Period" onClose={() => setSheet(null)}>
          {PERIODS.map(p => (
            <SheetRow key={p.value} icon={period === p.value ? Check : undefined} label={p.label} onClick={() => { changePeriod(p.value); if (p.value !== 'month') setSheet(null); }} />
          ))}
          {period === 'month' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <button type="button" onClick={() => setMonthOffset(o => o - 1)} aria-label="Previous month" className="btn btn-secondary" style={{ width: 48, height: 44, justifyContent: 'center' }}><ChevronLeft size={18} /></button>
              <div style={{ flex: 1, textAlign: 'center', fontWeight: 800 }}>{monthLabel(monthOffset)}</div>
              <button type="button" onClick={() => setMonthOffset(o => Math.min(0, o + 1))} disabled={monthOffset >= 0} aria-label="Next month" className="btn btn-secondary" style={{ width: 48, height: 44, justifyContent: 'center' }}><ChevronRight size={18} /></button>
            </div>
          )}
          {period === 'month' && <button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 46, justifyContent: 'center', marginTop: 12 }} onClick={() => setSheet(null)}>Done</button>}
        </AppSheet>
      )}

      {sheet === 'cash' && (
        <AppSheet title="Count cash" onClose={() => setSheet(null)}>
          <CashCounterFields onTotalChange={setCashTotal} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-success" disabled={!cashTotal} style={{ flex: 1, minHeight: 46, justifyContent: 'center' }} onClick={() => { setSheet(null); setModal({ type: 'income', amount: cashTotal }); }}>Add ₹{fmt(cashTotal)} as income</button>
            <button type="button" className="btn btn-danger" disabled={!cashTotal} style={{ flex: 1, minHeight: 46, justifyContent: 'center' }} onClick={() => { setSheet(null); setModal({ type: 'expense', amount: cashTotal }); }}>as expense</button>
          </div>
        </AppSheet>
      )}

      {sheet === 'newcat' && (
        <AppSheet title="New category" onClose={() => setSheet(null)}>
          <div style={{ marginBottom: 10 }}><Seg value={nc.type} onChange={t => setNc(n => ({ ...n, type: t }))} options={[['expense', 'Expense'], ['income', 'Income']]} /></div>
          <input autoFocus value={nc.name} onChange={e => setNc(n => ({ ...n, name: e.target.value }))} onKeyDown={e => e.key === 'Enter' && saveNewCat()} placeholder="Category name"
            style={{ width: '100%', boxSizing: 'border-box', height: 46, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', padding: '0 14px', fontSize: '16px' }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
            {CUSTOM_PALETTE.map(c => (
              <button key={c} type="button" aria-label={c} onClick={() => setNc(n => ({ ...n, color: c }))}
                style={{ width: 34, height: 34, borderRadius: '50%', background: c, border: nc.color === c ? '3px solid var(--text)' : '3px solid transparent', cursor: 'pointer' }} />
            ))}
          </div>
          <button type="button" className="btn btn-primary" disabled={!nc.name.trim()} style={{ width: '100%', minHeight: 46, justifyContent: 'center' }} onClick={saveNewCat}>Add category</button>
        </AppSheet>
      )}

      {actionTxn && (
        <AppSheet title={actionTxn.description} onClose={() => setActionTxn(null)}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 900, color: ink[actionTxn.type] }}>{sgn(actionTxn.type)}₹{fmt(actionTxn.amount)}</span>
            <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{new Date(actionTxn.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
          <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>
            {catOf(actionTxn.category).label} · <span style={{ textTransform: 'capitalize' }}>{actionTxn.paymentMethod?.replace('_', ' ')}</span>
            {actionTxn.reference ? ` · ${actionTxn.reference}` : ''}
          </div>
          <SheetRow icon={Edit2} label="Edit" onClick={() => { const t = actionTxn; setActionTxn(null); setModal(t); }} />
          <SheetRow icon={Check} label={actionTxn.checked ? 'Unmark as checked' : 'Mark as checked'} onClick={() => { toggleChecked(actionTxn); setActionTxn(null); }} />
          {actionTxn.receiptUrl && <SheetRow icon={Paperclip} label="View receipt" onClick={() => window.open(actionTxn.receiptUrl, '_blank', 'noreferrer')} />}
          <SheetRow icon={Trash2} label="Delete" danger onClick={() => { const id = actionTxn._id; setActionTxn(null); handleDelete(id); }} />
        </AppSheet>
      )}

      {modal && (
        <TransactionModal sheet initial={modal} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          onCategoryAdded={(name, color) => setCustomCatColors(prev => ({ ...prev, [name]: color }))} />
      )}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */
export default function MoneyManagerPage() {
  useDarkReady();
  const [period, setPeriod] = useState('month');
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current month, -1 = previous, etc. ('month' period only)
  const [activeTab, setActiveTab] = useState('all');
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { type } | txn object
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;

  // Colors for custom (user-added) categories, so the pie chart & transaction list
  // show the color a user actually picked instead of falling back to a hash.
  const [customCatColors, setCustomCatColors] = useState({});
  useEffect(() => {
    Promise.all([
      optionsAPI.get('moneyCategories_income'),
      optionsAPI.get('moneyCategories_expense'),
    ]).then(([inc, exp]) => {
      const map = {};
      [...(inc.data.data || []), ...(exp.data.data || [])].forEach(raw => {
        const c = parseCatOption(raw);
        map[c.value] = c.color;
      });
      setCustomCatColors(map);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const offset = period === 'month' ? monthOffset : undefined;
      const [s, t] = await Promise.all([
        moneyAPI.getSummary(period, offset),
        moneyAPI.getTransactions({ period, monthOffset: offset, type: activeTab === 'all' ? undefined : activeTab }),
      ]);
      setSummary(s.data.data);
      setTransactions(t.data.data);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  }, [period, monthOffset, activeTab]);

  useEffect(() => { load(); }, [load]);

  const changePeriod = (v) => { setPeriod(v); setMonthOffset(0); };

  const toggleChecked = async (txn) => {
    try {
      await moneyAPI.updateTransaction(txn._id, { checked: !txn.checked });
      setTransactions(list => list.map(t => t._id === txn._id ? { ...t, checked: !txn.checked } : t));
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await moneyAPI.deleteTransaction(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const periodLabel = period === 'month' ? monthLabel(monthOffset) : (PERIODS.find(p => p.value === period)?.label || '');

  const pieData = (summary?.categoryBreakdown || []).map(b => ({
    name: getCat(b._id.category, customCatColors).label,
    value: b.total,
    color: getCat(b._id.category, customCatColors).color,
    txnType: b._id.type,
  }));

  const grouped = groupByDate(transactions);

  const tabBtn = (v, l) => (
    <button onClick={() => setActiveTab(v)}
      style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '.82rem', fontWeight: 600, transition: 'all .15s',
        background: activeTab === v ? 'var(--cb-ffffff)' : 'transparent',
        color: activeTab === v ? 'var(--primary)' : 'var(--text-muted)',
        boxShadow: activeTab === v ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
      }}>
      {l}
    </button>
  );

  const periodBtn = (v, l) => (
    <button onClick={() => changePeriod(v)}
      style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
        fontSize: '.82rem', fontWeight: 600, transition: 'all .15s',
        background: period === v ? 'var(--cb-ffffff)' : 'transparent',
        color: period === v ? 'var(--primary)' : 'var(--text-muted)',
        boxShadow: period === v ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
      }}>
      {l}
    </button>
  );

  if (isApp) {
    return <MoneyApp {...{ summary, transactions, loading, period, changePeriod, monthOffset, setMonthOffset, periodLabel, customCatColors, setCustomCatColors, modal, setModal, load, toggleChecked, handleDelete }} />;
  }

  return (
    <div>

      {/* ─── Summary Tabs ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>

        {/* Income */}
        <div style={{ flex: '1 1 200px', background: 'var(--cb-f0fdf4)', border: '1.5px solid var(--cb-bbf7d0)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--cb-10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TrendingUp size={15} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-166534)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Income</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--c-15803d)', letterSpacing: '-.02em' }}>₹{fmt(summary?.periodIncome || 0)}</div>
          </div>
        </div>

        {/* Expense */}
        <div style={{ flex: '1 1 200px', background: 'var(--cb-fff1f2)', border: '1.5px solid var(--cb-fecdd3)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--cb-ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TrendingDown size={15} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--c-9f1239)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Expense</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--c-dc2626)', letterSpacing: '-.02em' }}>₹{fmt(summary?.periodExpense || 0)}</div>
          </div>
        </div>

        {/* Total (all-time balance) — last */}
        <div style={{ flex: '1 1 200px', background: 'linear-gradient(135deg, #0f172a 0%, var(--primary) 100%)', borderRadius: 12, padding: '12px 16px', color: 'var(--c-ffffff)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Wallet size={15} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, opacity: .7, textTransform: 'uppercase', letterSpacing: '.06em' }}>Total Balance</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 900, letterSpacing: '-.02em' }}>₹{fmt(summary?.balance || 0)}</div>
          </div>
        </div>
      </div>

      {/* ─── Controls ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: 'var(--cb-f1f5f9)', borderRadius: 8, padding: 3, gap: 2 }}>
          {PERIODS.map(p => periodBtn(p.value, p.label))}
        </div>

        {/* Month navigation — only meaningful when viewing a single month */}
        {period === 'month' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--cb-f1f5f9)', borderRadius: 8, padding: 3 }}>
            <button type="button" onClick={() => setMonthOffset(o => o - 1)} title="Previous month"
              style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 6, background: 'var(--cb-ffffff)', cursor: 'pointer', color: 'var(--text-muted)' }}>
              <ChevronLeft size={15} />
            </button>
            <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text)', padding: '0 6px', minWidth: 118, textAlign: 'center' }}>
              {monthLabel(monthOffset)}
            </span>
            <button type="button" onClick={() => setMonthOffset(o => Math.min(0, o + 1))} disabled={monthOffset >= 0} title="Next month"
              style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 6,
                background: monthOffset >= 0 ? 'transparent' : 'var(--cb-ffffff)', cursor: monthOffset >= 0 ? 'default' : 'pointer',
                color: monthOffset >= 0 ? 'var(--border)' : 'var(--text-muted)' }}>
              <ChevronRight size={15} />
            </button>
          </div>
        )}

        <div style={{ display: 'flex', background: 'var(--cb-f1f5f9)', borderRadius: 8, padding: 3, gap: 2 }}>
          {tabBtn('all', 'All')}
          {tabBtn('income', 'Income')}
          {tabBtn('expense', 'Expense')}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          <button className="btn btn-success" onClick={() => setModal({ type: 'income' })}>
            <Plus size={15} /> Add Income
          </button>
          <button className="btn btn-danger" onClick={() => setModal({ type: 'expense' })}>
            <Plus size={15} /> Add Expense
          </button>
        </div>
      </div>

      {/* ─── Content Grid ──────────────────────────────────────────────────── */}
      <div className="money-layout">

        {/* Left: Category Breakdown */}
        <div className="card">
          <div className="card-header"><h3>Category Breakdown</h3></div>
          <div style={{ padding: '16px 20px' }}>
            {pieData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
                No data for {periodLabel.toLowerCase()}
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={52} outerRadius={80}
                      dataKey="value" paddingAngle={3}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`₹${fmt(v)}`, '']} />
                  </PieChart>
                </ResponsiveContainer>

                <div style={{ marginTop: 4 }}>
                  {pieData.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{d.name}</span>
                      </div>
                      <span style={{ fontSize: '.8rem', fontWeight: 700, color: d.txnType === 'income' ? 'var(--c-15803d)' : 'var(--c-dc2626)' }}>
                        {d.txnType === 'income' ? '+' : '−'}₹{fmt(d.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right: Transactions */}
        <div className="card">
          <div className="card-header">
            <h3>Transactions</h3>
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{transactions.length} entries</span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : transactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--text-muted)' }}>
              <Wallet size={44} style={{ marginBottom: 12, opacity: .2 }} />
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No transactions yet</p>
              <p style={{ fontSize: '.82rem' }}>Add your first income or expense above</p>
            </div>
          ) : (
            Object.entries(grouped).map(([dateLabel, txns]) => {
              const dayIncome  = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
              const dayExpense = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
              return (
                <div key={dateLabel}>
                  {/* Date group header */}
                  <div style={{ padding: '9px 20px', background: 'var(--cb-f8fafc)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                      {dateLabel}
                    </span>
                    <div style={{ display: 'flex', gap: 12, fontSize: '.75rem', fontWeight: 700 }}>
                      {dayIncome  > 0 && <span style={{ color: 'var(--c-15803d)' }}>+₹{fmt(dayIncome)}</span>}
                      {dayExpense > 0 && <span style={{ color: 'var(--c-dc2626)' }}>−₹{fmt(dayExpense)}</span>}
                    </div>
                  </div>

                  {/* Individual transactions */}
                  {txns.map(txn => {
                    const cat = getCat(txn.category, customCatColors);
                    return (
                      <div key={txn._id}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: '1px solid var(--border)', transition: 'background .13s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--cb-f8fafc)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>

                        {/* Category indicator */}
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: cat.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <div style={{ width: 14, height: 14, borderRadius: 4, background: cat.color }} />
                        </div>

                        {/* Details */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {txn.description}
                          </div>
                          <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            {cat.label}
                            {' · '}
                            <span style={{ textTransform: 'capitalize' }}>{txn.paymentMethod?.replace('_', ' ')}</span>
                            {txn.reference && <span style={{ fontStyle: 'italic' }}> · {txn.reference}</span>}
                          </div>
                        </div>

                        {/* Amount + time */}
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: '1rem', color: txn.type === 'income' ? 'var(--c-15803d)' : 'var(--c-dc2626)' }}>
                            {txn.type === 'income' ? '+' : '−'}₹{fmt(txn.amount)}
                          </div>
                          <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 1 }}>
                            {new Date(txn.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>

                        {/* Edit / Delete */}
                        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                          {txn.receiptUrl && (
                            <a href={txn.receiptUrl} target="_blank" rel="noreferrer"
                              style={{ color: 'var(--text-muted)', padding: 5, borderRadius: 5, display: 'flex' }}
                              title="View receipt">
                              <Paperclip size={13} />
                            </a>
                          )}
                          <button onClick={() => setModal(txn)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 5, borderRadius: 5 }}
                            title="Edit">
                            <Edit2 size={13} />
                          </button>
                          <button onClick={() => toggleChecked(txn)}
                            style={{ background: txn.checked ? 'var(--c-15803d)' : 'none', border: 'none', cursor: 'pointer', color: txn.checked ? '#fff' : 'var(--text-muted)', padding: 5, borderRadius: 5 }}
                            title={txn.checked ? 'Checked (click to undo)' : 'Mark as checked'}>
                            <Check size={13} />
                          </button>
                          <button onClick={() => handleDelete(txn._id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 5, borderRadius: 5 }}
                            title="Delete">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ─── Modal ─────────────────────────────────────────────────────────── */}
      {modal && (
        <TransactionModal
          initial={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
          onCategoryAdded={(name, color) => setCustomCatColors(prev => ({ ...prev, [name]: color }))}
        />
      )}
    </div>
  );
}

// Standard current INR denominations, largest first. Embedded directly in the Add Income /
// Add Expense form's Amount field (both use TransactionModal below) rather than a separate
// tool — counts persist in localStorage (per browser) so switching between forms or a reload
// doesn't lose an in-progress count.
const DENOMINATIONS = [500, 200, 100, 50, 20, 10];
const CASH_COUNTER_STORAGE_KEY = 'moneyManager.cashCounter';

function loadCashCounterCounts() {
  try {
    const raw = localStorage.getItem(CASH_COUNTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function CashCounterFields({ onTotalChange }) {
  const [counts, setCounts] = useState(loadCashCounterCounts);
  const mounted = useRef(false);

  useEffect(() => {
    try { localStorage.setItem(CASH_COUNTER_STORAGE_KEY, JSON.stringify(counts)); } catch { /* ignore */ }
  }, [counts]);

  const total = DENOMINATIONS.reduce((sum, d) => sum + d * (counts[d] || 0), 0);

  // Push the live total into the Amount field on every count change — skip the very first
  // render so just opening the counter never stomps an amount already typed/edited.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    onTotalChange(total);
  }, [total]); // onTotalChange intentionally omitted — it's a fresh inline closure from the parent every render

  const setCount = (denom, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    setCounts(c => ({ ...c, [denom]: n }));
  };

  return (
    <div style={{ marginTop: 10, padding: 12, background: 'var(--cb-f8fafc)', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {DENOMINATIONS.map(d => (
          <div key={d}>
            <label style={{ fontSize: '.72rem', color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>₹{d} ×</label>
            <input type="number" min="0" className="form-control" style={{ padding: '5px 8px', fontSize: '.85rem' }}
              value={counts[d] || ''} placeholder="0"
              onChange={e => setCount(d, e.target.value)} />
          </div>
        ))}
      </div>
      <div style={{
        marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: '.8rem', fontWeight: 700 }}>Counted total: ₹{fmt(total)}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCounts({})}
          style={{ fontSize: '.75rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <RotateCcw size={11} />Clear
        </button>
      </div>
    </div>
  );
}

/* ─── Add / Edit Modal ───────────────────────────────────────────────────────── */
// sheet = app view: bottom sheet, category chips, and date/method/note/receipt tucked behind "More details".
function TransactionModal({ initial, onClose, onSaved, onCategoryAdded, sheet }) {
  const isEdit = !!initial?._id;
  const [moreOpen, setMoreOpen] = useState(isEdit);

  const [form, setForm] = useState({
    type:          initial?.type          || 'income',
    amount:        initial?.amount        ? String(initial.amount) : '',
    category:      initial?.category      || '',
    description:   initial?.description   || '',
    date:          initial?.date
                     ? new Date(initial.date).toISOString().slice(0, 10)
                     : new Date().toISOString().slice(0, 10),
    paymentMethod: initial?.paymentMethod || 'cash',
    reference:     initial?.reference     || '',
  });
  const [loading, setLoading] = useState(false);

  // Custom categories, per type, fetched from the shared options store and merged
  // with the built-in list — same "add your own" pattern as PaymentMethodSelect.
  const [customCats, setCustomCats] = useState({ income: [], expense: [] });
  const [addingCat, setAddingCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(CUSTOM_PALETTE[0]);
  const [savingCat, setSavingCat] = useState(false);

  useEffect(() => {
    Promise.all([
      optionsAPI.get('moneyCategories_income'),
      optionsAPI.get('moneyCategories_expense'),
    ]).then(([inc, exp]) => {
      setCustomCats({
        income:  (inc.data.data || []).map(parseCatOption),
        expense: (exp.data.data || []).map(parseCatOption),
      });
    }).catch(() => {});
  }, []);

  const baseCats = form.type === 'income' ? INCOME_CATS : EXPENSE_CATS;
  const cats = [...baseCats, ...customCats[form.type].filter(c => !baseCats.some(b => b.value === c.value))];

  // Optional receipt/bill photo
  const [showCounter, setShowCounter] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [removeReceipt, setRemoveReceipt] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const saveNewCategory = async () => {
    const n = newCatName.trim();
    if (!n) return;
    setSavingCat(true);
    try {
      await optionsAPI.add(`moneyCategories_${form.type}`, `${n}::${newCatColor}`);
      setCustomCats(prev => ({ ...prev, [form.type]: [...prev[form.type], { value: n, label: n, color: newCatColor }] }));
      onCategoryAdded?.(n, newCatColor);
      set('category', n);
      setNewCatName('');
      setNewCatColor(CUSTOM_PALETTE[0]);
      setAddingCat(false);
    } catch {
      toast.error('Failed to add category');
    } finally {
      setSavingCat(false);
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    setRemoveReceipt(false);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    if (initial?.receiptUrl) setRemoveReceipt(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!parseFloat(form.amount) > 0) { toast.error('Enter a valid amount'); return; }
    if (!form.category) { toast.error('Select a category'); return; }
    setLoading(true);
    try {
      let payload = form;
      if (receiptFile || removeReceipt) {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        if (receiptFile) fd.append('receipt', receiptFile);
        else fd.append('receiptUrl', '');
        payload = fd;
      }
      if (isEdit) {
        await moneyAPI.updateTransaction(initial._id, payload);
        toast.success('Transaction updated');
      } else {
        await moneyAPI.createTransaction(payload);
        toast.success(form.type === 'income' ? 'Income added' : 'Expense added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const inp = {
    width: '100%', padding: '9px 12px',
    border: '1px solid var(--border)', borderRadius: 8,
    fontSize: '.88rem', boxSizing: 'border-box', fontFamily: 'inherit',
    outline: 'none',
  };

  const isIncome = form.type === 'income';

  return (
    <div className={`modal-overlay${sheet ? ' mm-sheet' : ''}`} onClick={onClose}>
      <div className="modal" style={sheet ? undefined : { maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Transaction' : `Add ${isIncome ? 'Income' : 'Expense'}`}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Type toggle — only for new */}
            {!isEdit && (
              <div style={{ display: 'flex', background: 'var(--cb-f1f5f9)', borderRadius: 10, padding: 4, gap: 4 }}>
                {['income', 'expense'].map(t => (
                  <button key={t} type="button"
                    onClick={() => setForm(f => ({ ...f, type: t, category: '' }))}
                    style={{ flex: 1, padding: '10px', borderRadius: 7, border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: '.88rem', transition: 'all .15s',
                      background: form.type === t ? (t === 'income' ? 'var(--cb-10b981)' : 'var(--cb-ef4444)') : 'transparent',
                      color: form.type === t ? '#fff' : 'var(--text-muted)',
                    }}>
                    {t === 'income' ? '↑ Income' : '↓ Expense'}
                  </button>
                ))}
              </div>
            )}

            {/* Amount */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: '.8rem', fontWeight: 600 }}>Amount (₹) *</label>
                <button type="button" onClick={() => setShowCounter(s => !s)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: '.76rem', fontWeight: 600, color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Banknote size={12} />{showCounter ? 'Hide cash counter' : 'Count cash'}
                </button>
              </div>
              <input style={{ ...inp, fontSize: '1.5rem', fontWeight: 900, padding: '14px 16px',
                color: isIncome ? '#15803d' : '#dc2626',
                borderColor: isIncome ? 'var(--cb-bbf7d0)' : 'var(--cb-fecdd3)',
                background: isIncome ? 'var(--cb-f0fdf4)' : 'var(--cb-fff1f2)',
              }}
                type="number" inputMode="decimal" step="0.01" min="0.01" required placeholder="0.00"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                autoFocus={!isEdit}
              />
              {showCounter && <CashCounterFields onTotalChange={(t) => set('amount', String(t))} />}
            </div>

            {/* Category grid */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 8 }}>Category *</label>
              <div style={sheet ? { display: 'flex', flexWrap: 'wrap', gap: 8 } : { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {cats.map(c => (
                  <button key={c.value} type="button"
                    onClick={() => set('category', c.value)}
                    style={{ padding: sheet ? '9px 14px' : '8px 10px', borderRadius: sheet ? 999 : 8, cursor: 'pointer',
                      border: `1.5px solid ${form.category === c.value ? c.color : 'var(--border)'}`,
                      background: form.category === c.value ? c.color + '18' : 'var(--cb-ffffff)',
                      color: form.category === c.value ? c.color : 'var(--text)',
                      fontSize: '.78rem', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left',
                      transition: 'all .13s',
                    }}>
                    <div style={{ width: 9, height: 9, borderRadius: 3, background: c.color, flexShrink: 0 }} />
                    {c.label}
                  </button>
                ))}
                <button type="button" onClick={() => setAddingCat(a => !a)}
                  style={{ padding: sheet ? '9px 14px' : '8px 10px', borderRadius: sheet ? 999 : 8, cursor: 'pointer',
                    border: `1.5px dashed ${addingCat ? 'var(--accent)' : 'var(--border)'}`,
                    background: addingCat ? 'var(--cb-e0f2fe)' : 'var(--cb-ffffff)',
                    color: addingCat ? '#0284c7' : 'var(--text-muted)',
                    fontSize: '.78rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 7, textAlign: 'left',
                  }}>
                  <Plus size={12} /> New Category
                </button>
              </div>
              {addingCat && (
                <div style={{ marginTop: 8, border: '1px solid var(--cb-93c5fd)', borderRadius: 8, padding: 8 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      autoFocus
                      value={newCatName}
                      onChange={e => setNewCatName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveNewCategory(); } if (e.key === 'Escape') { setAddingCat(false); setNewCatName(''); } }}
                      placeholder="New category name…"
                      style={{ flex: 1, border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: '6px 10px', fontSize: '.82rem', outline: 'none' }}
                    />
                    <button type="button" onClick={saveNewCategory} disabled={savingCat || !newCatName.trim()}
                      style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: 'var(--c-ffffff)', cursor: 'pointer', fontSize: '.82rem', fontWeight: 600, opacity: !newCatName.trim() ? 0.5 : 1 }}>
                      {savingCat ? '…' : 'Add'}
                    </button>
                    <button type="button" onClick={() => { setAddingCat(false); setNewCatName(''); }}
                      style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--cb-e5e7eb)', background: 'transparent', cursor: 'pointer', color: 'var(--c-6b7280)' }}>
                      <X size={13} />
                    </button>
                  </div>

                  {/* Color picker for the new category */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginRight: 2 }}>Color:</span>
                    {CUSTOM_PALETTE.map(c => (
                      <button key={c} type="button" onClick={() => setNewCatColor(c)} title={c}
                        style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
                          border: newCatColor === c ? '2px solid var(--text)' : '2px solid transparent',
                          boxShadow: newCatColor === c ? '0 0 0 1.5px ' + c : 'none',
                        }}
                      />
                    ))}
                    <label title="Custom color" style={{ width: 20, height: 20, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer',
                      border: !CUSTOM_PALETTE.includes(newCatColor) ? '2px solid var(--text)' : '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <input type="color" value={newCatColor} onChange={e => setNewCatColor(e.target.value)}
                        style={{ position: 'absolute', inset: -4, border: 'none', padding: 0, cursor: 'pointer' }} />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Description *</label>
              <input style={inp} required placeholder="What is this transaction for?"
                value={form.description} onChange={e => set('description', e.target.value)} />
            </div>

            {sheet && (
              <button type="button" onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44, padding: '0 12px', borderRadius: 10,
                  border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer', fontSize: '.84rem', fontWeight: 600 }}>
                <span>More details <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {form.date === new Date().toISOString().slice(0, 10) ? 'Today' : form.date} · {form.paymentMethod.replace('_', ' ')}</span></span>
                <span>{moreOpen ? '▴' : '▾'}</span>
              </button>
            )}

            <div style={{ display: sheet && !moreOpen ? 'none' : 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Date + Method */}
            <div style={{ display: 'grid', gridTemplateColumns: sheet ? '1fr' : '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Date *</label>
                <input style={inp} type="date" required value={form.date} onChange={e => set('date', e.target.value)} onInvalid={() => setMoreOpen(true)} />
              </div>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Payment Method</label>
                {sheet ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {METHODS.map(m => (
                      <button key={m} type="button" onClick={() => set('paymentMethod', m)}
                        style={{ padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontSize: '.8rem', fontWeight: 600, textTransform: 'capitalize',
                          border: `1.5px solid ${form.paymentMethod === m ? 'var(--primary)' : 'var(--border)'}`,
                          background: form.paymentMethod === m ? 'var(--primary)' : 'var(--bg-card)',
                          color: form.paymentMethod === m ? '#fff' : 'var(--text)' }}>
                        {m.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                ) : (
                  <select style={inp} value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)}>
                    {METHODS.map(m => (
                      <option key={m} value={m}>{m.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Reference */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Reference / Note (optional)</label>
              <input style={inp} placeholder="Invoice no, receipt no, remarks..."
                value={form.reference} onChange={e => set('reference', e.target.value)} />
            </div>

            {/* Receipt / bill photo — optional */}
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Receipt Photo (optional)</label>
              {receiptPreview || (initial?.receiptUrl && !removeReceipt) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <img src={receiptPreview || initial.receiptUrl} alt="Receipt"
                    style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }} />
                  <button type="button" onClick={clearReceipt}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--cb-e5e7eb)', background: 'transparent', cursor: 'pointer', color: 'var(--c-6b7280)', fontSize: '.8rem' }}>
                    <X size={13} /> Remove
                  </button>
                </div>
              ) : (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1.5px dashed var(--border)', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '.83rem' }}>
                  <ImageIcon size={16} />
                  Attach a photo of the bill/receipt
                  <input type="file" accept="image/*" onChange={handleReceiptChange} style={{ display: 'none' }} />
                </label>
              )}
            </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={loading}
              className={`btn ${isIncome ? 'btn-success' : 'btn-danger'}`}>
              {loading ? 'Saving...' : isEdit ? 'Update Transaction' : `Add ${isIncome ? 'Income' : 'Expense'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
